import React, { useState } from 'react';
import { Box, Clipboard, HStack } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { LuThumbsDown, LuThumbsUp } from 'react-icons/lu';
import { Tooltip } from '@/components/ui/tooltip';
import ChatFeedbackDialog from '../ChatFeedbackDialog';
import { useFeedback, FEEDBACK_RATING } from '../useFeedback';
import type { FeedbackRating } from '@/client';

interface ResponseActionsProps {
	/** The server-side exchange ID used to submit and look up feedback */
	exchangeId: string;
	/** Plain text of the response, used for clipboard copy */
	responseText: string;
	/** Optional sources trigger rendered inline in the action row. */
	sources?: React.ReactNode;
	/** Optional applied-corrections trigger — the third evidence channel. */
	corrections?: React.ReactNode;
	/** Optional web-sources trigger — a distinct provenance class, never merged with `sources`. */
	webSources?: React.ReactNode;
}

const ACTION_BUTTON_HEIGHT = 9;
const ACTION_BUTTON_WIDTH = 10;
const ACTION_BUTTON_VISUAL_SIZE = '36px';
const ACTION_ICON_SIZE = '16px';
const ACTION_MUTED_COLOR = 'fg.muted';

interface ActionButtonVisualProps {
	children: React.ReactNode;
	isActive?: boolean;
	isDisabled?: boolean;
}

const ActionButtonVisual: React.FC<ActionButtonVisualProps> = ({ children, isActive = false, isDisabled = false }) => (
	<Box
		boxSize={ACTION_BUTTON_VISUAL_SIZE}
		borderRadius="full"
		display="flex"
		alignItems="center"
		justifyContent="center"
		bg={isActive ? 'brand.muted' : 'transparent'}
		transitionProperty="background-color"
		transitionDuration="fast"
		_groupHover={isDisabled ? undefined : { bg: 'brand.muted' }}
	>
		{children}
	</Box>
);

/**
 * Action bar rendered below each assistant response.
 * Follows the Claude/ChatGPT UX: copy, thumbs up, thumbs down.
 * Clicking a thumb opens a small optional-comment dialog before submitting.
 */
const ChatResponseActions: React.FC<ResponseActionsProps> = ({
	exchangeId,
	responseText,
	sources,
	corrections,
	webSources,
}) => {
	const { existingFeedback, submit, isPending, canSubmit } = useFeedback(exchangeId);
	const [pendingRating, setPendingRating] = useState<FeedbackRating | null>(null);

	// Feedback is immutable once submitted (the backend is idempotent on exchange_id), so lock the
	// thumbs after a rating exists — the chosen one stays highlighted, the other dims.
	const hasFeedback = !!existingFeedback;

	const handleThumbClick = (rating: FeedbackRating) => {
		// If already rated with this rating, do nothing (idempotent UX)
		if (existingFeedback?.rating === rating) return;
		setPendingRating(rating);
	};

	const handleDialogSubmit = (comment?: string) => {
		if (!pendingRating) return;
		submit(pendingRating, comment);
		setPendingRating(null);
	};

	const handleDialogOpenChange = (open: boolean) => {
		if (!open) setPendingRating(null);
	};

	return (
		<>
			<HStack gap={0.5} mt={1}>
				<Clipboard.Root value={responseText}>
					{/* Tooltip reflects the clipboard state: the trigger swaps to a tick once copied,
					    so the label follows suit instead of still saying "Copy response". */}
					<Clipboard.Context>
						{({ copied }) => (
							<Tooltip
								content={copied ? 'Response copied' : 'Copy response'}
								openDelay={300}
								closeDelay={100}
							>
								<Clipboard.Trigger asChild>
									<IconButton
										className="group"
										aria-label="Copy response"
										variant="ghost"
										color={ACTION_MUTED_COLOR}
										h={ACTION_BUTTON_HEIGHT}
										w={ACTION_BUTTON_WIDTH}
										minW={ACTION_BUTTON_WIDTH}
										p={0}
										// Wider touch target, but the visible hover stays a circle: the
										// button itself doesn't tint (transparent hover) — the nested 36px
										// circle does, via _groupHover.
										_hover={{ bg: 'transparent' }}
										css={{ '& svg': { width: ACTION_ICON_SIZE, height: ACTION_ICON_SIZE } }}
									>
										<ActionButtonVisual>
											<Clipboard.Indicator />
										</ActionButtonVisual>
									</IconButton>
								</Clipboard.Trigger>
							</Tooltip>
						)}
					</Clipboard.Context>
				</Clipboard.Root>

				{canSubmit && (
					<>
						<Tooltip content="Good response" openDelay={300} closeDelay={100}>
							<IconButton
								className="group"
								aria-label="Thumbs up"
								variant="ghost"
								h={ACTION_BUTTON_HEIGHT}
								w={ACTION_BUTTON_WIDTH}
								minW={ACTION_BUTTON_WIDTH}
								p={0}
								_hover={{ bg: 'transparent' }}
								color={
									existingFeedback?.rating === FEEDBACK_RATING.THUMBS_UP
										? 'brand.fg'
										: ACTION_MUTED_COLOR
								}
								disabled={hasFeedback}
								css={{ '& svg': { width: ACTION_ICON_SIZE, height: ACTION_ICON_SIZE } }}
								_disabled={{
									opacity: existingFeedback?.rating === FEEDBACK_RATING.THUMBS_UP ? 1 : 0.4,
									cursor: 'default',
								}}
								onClick={() => handleThumbClick(FEEDBACK_RATING.THUMBS_UP)}
							>
								<ActionButtonVisual
									isActive={existingFeedback?.rating === FEEDBACK_RATING.THUMBS_UP}
									isDisabled={hasFeedback}
								>
									<LuThumbsUp />
								</ActionButtonVisual>
							</IconButton>
						</Tooltip>

						<Tooltip content="Bad response" openDelay={300} closeDelay={100}>
							<IconButton
								className="group"
								aria-label="Thumbs down"
								variant="ghost"
								h={ACTION_BUTTON_HEIGHT}
								w={ACTION_BUTTON_WIDTH}
								minW={ACTION_BUTTON_WIDTH}
								p={0}
								_hover={{ bg: 'transparent' }}
								color={
									existingFeedback?.rating === FEEDBACK_RATING.THUMBS_DOWN
										? 'brand.fg'
										: ACTION_MUTED_COLOR
								}
								disabled={hasFeedback}
								css={{ '& svg': { width: ACTION_ICON_SIZE, height: ACTION_ICON_SIZE } }}
								_disabled={{
									opacity: existingFeedback?.rating === FEEDBACK_RATING.THUMBS_DOWN ? 1 : 0.4,
									cursor: 'default',
								}}
								onClick={() => handleThumbClick(FEEDBACK_RATING.THUMBS_DOWN)}
							>
								<ActionButtonVisual
									isActive={existingFeedback?.rating === FEEDBACK_RATING.THUMBS_DOWN}
									isDisabled={hasFeedback}
								>
									<LuThumbsDown />
								</ActionButtonVisual>
							</IconButton>
						</Tooltip>
					</>
				)}

				{sources}
				{corrections}
				{webSources}
			</HStack>

			{pendingRating && (
				<ChatFeedbackDialog
					open={!!pendingRating}
					rating={pendingRating}
					isPending={isPending}
					onOpenChange={handleDialogOpenChange}
					onSubmit={handleDialogSubmit}
				/>
			)}
		</>
	);
};

export default ChatResponseActions;
